import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, useWindowDimensions, View, Text } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { Button } from "../ui/button";
import DownloadIcon from "@/assets/svgs/download-icon";

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type DownloadStudyMaterialSheetPayload = {
  fileName: string;
  url: string;
};

export type DownloadStudyMaterialSheetResult = void;

function DownloadStudyMaterialSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    DownloadStudyMaterialSheetPayload,
    DownloadStudyMaterialSheetResult
  >(SheetIds.DOWNLOAD_STUDY_MATERIAL_SHEET);
  const responsiveWidth = useResponsiveWidth();
  const { fileName, url } = (sheet?.payload ??
    {}) as DownloadStudyMaterialSheetPayload;
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetView>
        <View className="bg-teachLoop-creamBase rounded-t-[20]">
          <View className="h-6" />
          <View className="mx-5 flex-row justify-between">
            <Text className="text-xl font-geologica-medium font-medium text-black">
              Download study material
            </Text>
            <Pressable onPress={onClose} className="active:opacity-80">
              <CloseIcon
                className="size-6"
                pathClassName="stroke-teachLoop-textPrimary"
              />
            </Pressable>
          </View>
          <View className="h-8" />
          <View className="mx-5 gap-8">
            <Text className="font-geologica-regular text-black text-base leading-[1.5]">
              {fileName}
            </Text>

            <View className="items-end">
              <Button
                size="sm"
                icon
                onPress={() => {
                  close();
                  window.open(url, "_blank");
                }}
              >
                <Button.Icon as={DownloadIcon} />
                <Button.Text size="sm">Download</Button.Text>
              </Button>
            </View>
          </View>
          <View className="h-8" />
        </View>
      </BottomSheetView>
    </BottomSheet>
  ) : null;
}

export default DownloadStudyMaterialSheet;
